import { inject, Injectable } from '@angular/core';
import { userDtos, webDtos } from '@vectronic/openApi-customer';
import { baseHref } from '@vectronic/ui';
import { DateTimeHelper, TimeService, Translator } from '@vectronic/util';
import { L } from '../leaflet-setup/leaflet-setup';

@Injectable({ providedIn: 'root' })
export class MapMarkerService {
  private timeService = inject(TimeService);

  private map: L.Map;
  private userPreferences: userDtos.UserPreferences;

  readonly speciesZoomConfig: { threshold: number; size: [number, number] }[] = [
    { threshold: 20, size: [75, 80] },
    { threshold: 18, size: [70, 75] },
    { threshold: 16, size: [65, 70] },
    { threshold: 14, size: [55, 60] },
    { threshold: 10, size: [45, 50] },
    { threshold: 6, size: [35, 40] },
    { threshold: 4, size: [25, 30] },
  ];

  readonly mortalityZoomConfig: { threshold: number; size: [number, number] }[] = [
    { threshold: 20, size: [60, 65] },
    { threshold: 18, size: [55, 60] },
    { threshold: 14, size: [45, 50] },
    { threshold: 10, size: [35, 40] },
    { threshold: 6, size: [25, 30] },
    { threshold: 4, size: [20, 25] },
  ];

  readonly lightConditionZoomConfig: { threshold: number; size: [number, number] }[] = [
    { threshold: 20, size: [16, 16] },
    { threshold: 16, size: [14, 14] },
    { threshold: 12, size: [12, 12] },
    { threshold: 10, size: [10, 10] },
    { threshold: 8, size: [8, 8] },
    { threshold: 6, size: [4, 4] },
  ];

  popupCustomizer?: (position: webDtos.GPSPosition, defaultHtml: string) => string;

  private lightConditionIcons: { [key: string]: L.Icon } = {};

  initialize(map: L.Map, userPreferences: userDtos.UserPreferences) {
    this.map = map;
    this.userPreferences = userPreferences;
  }

  markerPopup(collarName: string, position: webDtos.GPSPosition): string {
    const { idDevice, mortalityStatus, acquisitionTime, latitude, longitude, altitude } = position;
    const utcTime = DateTimeHelper.formatDateTime(acquisitionTime, true);
    let lmtTime = '';
    if (acquisitionTime) {
      lmtTime = this.timeService.convertToLocalTime(
        acquisitionTime + (acquisitionTime?.slice(-1) !== 'Z' ? 'Z' : ''),
      );
    }

    const defaultHtml =
      `<b>Name: </b> ${collarName} <br>` +
      `<b>Collar ID: </b> ${idDevice} <br>` +
      `<b>Status: </b> ${Translator.mortalityStatusShort(mortalityStatus)} <br>` +
      `<b>UTC: </b> ${utcTime} <br>` +
      (this.timeService.shouldShowLocalTime() ? `<b>LMT: </b> ${lmtTime} <br>` : '') +
      `<b>Lat: </b> ${latitude.toFixed(5)}&deg; <br>` +
      `<b>Lon: </b> ${longitude.toFixed(5)}&deg; <br>` +
      `<b>Height: </b> ${altitude}m`;

    return this.popupCustomizer ? this.popupCustomizer(position, defaultHtml) : defaultHtml;
  }

  createColoredSvgIcon(svgString: string, color: string): L.Icon {
    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(svgString, 'image/svg+xml');
    const pathElements = svgDoc.querySelectorAll('path');
    pathElements.forEach((path) => {
      path.setAttribute('fill', this.darkenColor(color, 0.3));
    });

    const styledSvgString = new XMLSerializer().serializeToString(svgDoc);
    const iconUrl = 'data:image/svg+xml,' + encodeURIComponent(styledSvgString);

    return L.icon({
      iconUrl,
      iconSize: this.getIconSizeFromConfig(this.speciesZoomConfig),
    });
  }

  darkenColor(hex: string, percent: number): string {
    if (!hex) return '#000000';
    const cleanHex = hex.replace('#', '');

    const r = parseInt(cleanHex.substring(0, 2), 16);
    const g = parseInt(cleanHex.substring(2, 4), 16);
    const b = parseInt(cleanHex.substring(4, 6), 16);

    const newR = Math.max(0, Math.min(255, Math.floor(r * (1 - percent))));
    const newG = Math.max(0, Math.min(255, Math.floor(g * (1 - percent))));
    const newB = Math.max(0, Math.min(255, Math.floor(b * (1 - percent))));

    return `#${newR.toString(16).padStart(2, '0')}${newG
      .toString(16)
      .padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
  }

  getIconSizeFromConfig(
    zoomConfig: { threshold: number; size: [number, number] }[],
  ): [number, number] {
    if (!this.map || !zoomConfig || zoomConfig.length === 0) {
      return [35, 40]; // Fallback size from speciesZoomConfig
    }
    const zoom = this.map.getZoom();
    if (zoom >= zoomConfig[0].threshold) {
      return zoomConfig[0].size;
    }
    for (const config of zoomConfig) {
      if (zoom >= config.threshold) {
        return config.size;
      }
    }
    return zoomConfig[zoomConfig.length - 1].size;
  }

  marker(
    position: webDtos.GPSPosition,
    index: number,
    mapPositionList: webDtos.MapPositionsList,
  ): { standard: L.Marker | L.CircleMarker; endpoint?: L.CircleMarker } {
    const { name, positions, color } = mapPositionList;
    const createPopupContent = () => this.markerPopup(name, position);
    const hideTrack = (mapPositionList as { hideTrack?: boolean }).hideTrack;
    const isNewest = !hideTrack && index === 0;
    const isOldest = !hideTrack && index === positions.length - 1;
    let endpointMarker;

    const standardMarker = this.standardMarker(position, color).bindPopup(createPopupContent);

    if (isNewest) {
      endpointMarker = this.newestMarker(position);
    } else if (isOldest) {
      endpointMarker = this.oldestMarker(position);
    }

    if (endpointMarker) {
      endpointMarker.bindPopup(createPopupContent);
    }
    return { standard: standardMarker, endpoint: endpointMarker };
  }

  standardMarker({ latitude, longitude }: webDtos.GPSPosition, fillColor = '0000ff') {
    if (fillColor[0] !== '#') fillColor = '#' + fillColor;
    const marker = L.circleMarker([latitude, longitude], {
      fillColor: fillColor,
      stroke: false,
      fill: true,
      fillOpacity: 1,
      radius: 4,
    });
    return marker;
  }

  newestMarker(position: webDtos.GPSPosition) {
    const { newestMarkerColor } = this.userPreferences;
    const color = '#' + (newestMarkerColor || 'ff0000');
    return this._estMarker(position, color);
  }

  oldestMarker(position: webDtos.GPSPosition) {
    const { oldestMarkerColor } = this.userPreferences;
    const color = '#' + (oldestMarkerColor || '00ff00');
    return this._estMarker(position, color);
  }

  private _estMarker({ latitude, longitude }: webDtos.GPSPosition, color: string): L.CircleMarker {
    const options: L.CircleMarkerOptions = { color, radius: 5 };
    return L.circleMarker([latitude, longitude], options);
  }

  _lightConditionMarker(position: webDtos.GPSPosition, collarName: string): L.Marker | undefined {
    const { latitude, longitude, lightCondition } = position;

    if (!lightCondition || lightCondition === 'UNDEFINED' || lightCondition === 'UNKNOWN') {
      return;
    }

    const allIconSizes = this.getAllIconSizes();
    const specificIconSize =
      allIconSizes[`lightCondition_${lightCondition}`] || allIconSizes['lightCondition'];

    const iconUrl = this.getLightConditionIconPath(lightCondition);
    if (!iconUrl) return;

    const cacheKey = `${lightCondition}_${specificIconSize[0]}x${specificIconSize[1]}`;
    if (!this.lightConditionIcons[cacheKey]) {
      this.lightConditionIcons[cacheKey] = L.icon({
        iconUrl,
        iconSize: specificIconSize,
        iconAnchor: [specificIconSize[0] / 2, specificIconSize[1] / 2],
      });
    }

    const icon = this.lightConditionIcons[cacheKey];

    const marker: L.Marker & { lightCondition?: string } = L.marker([latitude, longitude], {
      icon: icon,
      interactive: false,
    });
    marker.lightCondition = lightCondition;

    return marker.bindPopup(() => this.markerPopup(collarName, position));
  }

  getLightConditionIconPath(lightCondition: string): string | undefined {
    const lightConditionIconPaths = {
      BRIGHT: baseHref + 'assets/img/sunny.svg',
      DARK: baseHref + 'assets/img/night.svg',
      TWILIGHT: baseHref + 'assets/img/dawn.svg',
    };
    return lightConditionIconPaths[lightCondition];
  }

  getAllIconSizes(): { [key: string]: [number, number] } {
    const lightConditionSize = this.getIconSizeFromConfig(this.lightConditionZoomConfig);
    const speciesIconSize = this.getIconSizeFromConfig(this.speciesZoomConfig);

    const twilightSize: [number, number] = [
      lightConditionSize[0] * 1.3,
      lightConditionSize[1] * 1.3,
    ];

    return {
      speciesIcon: speciesIconSize,
      mortality: this.getIconSizeFromConfig(this.mortalityZoomConfig),
      lightCondition_BRIGHT: lightConditionSize,
      lightCondition_DARK: lightConditionSize,
      lightCondition_TWILIGHT: twilightSize,
    };
  }
}
